<template>
  <button
    type="button"
    class="file-link"
    :class="{ failed }"
    :disabled="downloading"
    :title="isImage ? 'Ver' : 'Descargar'"
    @click="open"
  >
    <PortalIcon :name="failed ? 'alert' : isImage ? 'eye' : 'download'" :size="14" />
    <span>{{ text }}</span>
  </button>
</template>

<script>
import * as candidateApi from "../../services/candidateApi";
import PortalIcon from "./PortalIcon.vue";
import { isImageFile } from "./candidateSummary";

// A stored document's button: images open in the portal's viewer (CandidatePortal's viewDocument);
// anything else (PDF, Word) can't be shown there, so it downloads right away.
export default {
  name: "FileLink",
  components: { PortalIcon },
  inject: ["viewDocument"],
  props: {
    // Document name, shown as the viewer's title.
    title: { type: String, required: true },
    // Every file of that document ([{ label, prefix }], e.g. both sides of the cédula),
    // so the viewer can switch between them.
    files: { type: Array, required: true },
    // The one this button opens.
    index: { type: Number, default: 0 },
    label: { type: String, default: "Ver" },
  },
  data() {
    return {
      downloading: false,
      failed: false,
      failedTimer: null,
    };
  },
  computed: {
    file() {
      return this.files[this.index];
    },
    isImage() {
      return isImageFile(this.file.prefix);
    },
    text() {
      if (this.failed) return "No se pudo descargar";
      if (this.downloading) return "Descargando…";
      // A side's own name ("Frente", "Reverso") still reads right; the icon tells what it does.
      return this.isImage || this.files.length > 1 ? this.label : "Descargar";
    },
  },
  beforeDestroy() {
    clearTimeout(this.failedTimer);
  },
  methods: {
    open() {
      if (!this.isImage) {
        this.download();
        return;
      }
      // The viewer only switches between the document's images.
      const images = this.files.filter((file) => isImageFile(file.prefix));
      this.viewDocument({ title: this.title, files: images, index: images.indexOf(this.file) });
    },
    // On this same page: stored files are binary/octet-stream, so following their signed link
    // starts a download instead of leaving the portal (and no new tab is needed).
    download() {
      this.downloading = true;
      this.failed = false;
      candidateApi
        .getFileUrl(this.file.prefix)
        .then((response) => {
          const url = response.data;
          if (!url) throw new Error("file url missing");
          const link = document.createElement("a");
          link.href = url;
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.failed = true;
          clearTimeout(this.failedTimer);
          this.failedTimer = setTimeout(() => {
            this.failed = false;
          }, 4000);
        })
        .finally(() => {
          this.downloading = false;
        });
    },
  },
};
</script>

<style scoped>
.file-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #fff;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #0e1424;
  white-space: nowrap;
  cursor: pointer;
}
.file-link:hover:not(:disabled) {
  background: #f4f5f8;
}
.file-link:disabled {
  cursor: wait;
  opacity: 0.7;
}
.file-link.failed {
  border-color: #f6c9c9;
  color: #c42b2b;
}
</style>
